import { showError } from '@components/app-error';
import { useBasicAutosaver } from '@components/autosaver';
import { showDialog } from '@components/dialog';
import {
  PageSelection,
  SalesBlockDefinition,
  PageBuilderProvider,
  scrollToSelectedSection,
} from '@course/components/page-builder';
import { showToast } from '@components/toaster';
import { isNetworkError } from 'client/lib/ajax';
import { substateUpdater, useDisposableMemo, useUndoRedo } from 'client/lib/hooks';
import { useEsc } from 'client/utils/use-esc';
import { on } from 'minidoc-editor';
import { ComponentChildren } from 'preact';
import { Dispatch, StateUpdater, useEffect, useMemo, useState } from 'preact/hooks';
import type { PageTheme, SalesBlockState, SalespageContent } from 'server/types';
import { Color } from 'shared/colors';
import { dissoc } from 'shared/utils';
import { PageEditorToolbar } from './page-editor-toolbar';
import { PageContent } from './page-content';
import { BlockList } from './block-list';
import { NewSectionModal } from '@course/components/page-builder';
import { UndoNotice } from '@course/components/undo-notice';
import { useDidUpdateEffect } from 'client/utils/use-did-update-effect';

export type SalesPageState<T = SalespageContent> = {
  content: T;
  selectedId: string;
  selection: PageSelection;
  viewMode?: 'desktop' | 'mobile';
  theme?: PageTheme;
};

type ViewState = {
  showSettingsForId: string;
  showUndoNotice: boolean;
  undoRedoCount: number;
  showMobileMenu: boolean;
  showNewSectionModal: { blockId?: string } | undefined;
};

type Props = {
  title: string;
  showCopyButton?: boolean;
  publicURL: string;
  state: SalesPageState;
  setState: Dispatch<StateUpdater<SalesPageState>>;
  onReset?(): void;
  blockDefinitions: Record<string, SalesBlockDefinition>;
  isDirty: boolean;
  isConnected: boolean;
  header?: ComponentChildren;
  customNavItems?: ComponentChildren;
  fontsLoaded?: boolean;
};

export function usePageAutosaver({
  content,
  onSave,
}: {
  content: SalesPageState['content'];
  onSave(opts: Pick<SalesPageState, 'content'>): Promise<unknown>;
}) {
  return useBasicAutosaver(content, (val) => {
    const promise = onSave({ content: val });

    promise.catch((err) => {
      if (!isNetworkError(err)) {
        showError(err);
      }
    });

    return promise;
  });
}

export function PageEditor({
  title,
  state,
  setState,
  onReset,
  isDirty,
  isConnected,
  blockDefinitions,
  showCopyButton,
  publicURL,
  header,
  customNavItems,
  fontsLoaded = true,
}: Props) {
  const { content, selectedId, viewMode = 'desktop' } = state;
  const [viewState, setViewState] = useState<ViewState>({
    showSettingsForId: '',
    showUndoNotice: false,
    undoRedoCount: 0,
    showMobileMenu: false,
    showNewSectionModal: undefined,
  });

  const undoState = {
    content: state.content,
    selection: state.selection,
  };
  const undoRedo = useUndoRedo(undoState, (newState: typeof undoState) => {
    setState((s) => ({ ...s, ...newState }));
    setViewState((v) => ({ ...v, undoRedoCount: v.undoRedoCount + 1 }));
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      const node = document.getSelection()?.focusNode;
      const parent = node?.parentElement;
      const focus = node instanceof HTMLElement ? node : parent;
      focus?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    return () => clearTimeout(timeout);
  }, [viewState.undoRedoCount]);

  /**
   * Resets the course sales page to the original placeholder default state.
   */
  async function onResetSalesPage() {
    if (!onReset) {
      return;
    }

    const lowerTitle = title.toLocaleLowerCase();
    const confirmed = await showDialog({
      mode: 'warn',
      title: `Reset ${lowerTitle} content?`,
      children: `This will delete all of your ${lowerTitle} content.`,
      confirmButtonText: `Yes, reset my ${lowerTitle}`,
      cancelButtonText: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    try {
      onReset();
      showToast({
        type: 'ok',
        title: `${title} reset`,
        message: `The ${lowerTitle} has been reset successfully.`,
      });
    } catch (err) {
      showError(err);
    }
  }

  useEffect(() => {
    if (!viewState.showUndoNotice) {
      return;
    }
    const timeout = setTimeout(() => setViewState((v) => ({ ...v, showUndoNotice: false })), 2000);
    return () => clearTimeout(timeout);
  }, [viewState.showUndoNotice]);

  useEsc(() => setViewState((v) => ({ ...v, showMobileMenu: false })), {
    escapeOnly: !viewState.showMobileMenu,
  });

  function setSelectedId(id: string) {
    setState((s) => (id === s.selectedId ? s : { ...s, selectedId: id }));
  }

  function setViewMode(viewMode: 'desktop' | 'mobile') {
    setState((s) => ({ ...s, viewMode }));
  }

  function setSelection(selection: PageSelection) {
    setState((s) => ({ ...s, selection }));
  }

  const setContent = useMemo(
    () =>
      substateUpdater(
        setState,
        (s) => s.content,
        (s, sub) => ({ ...s, content: sub }),
      ),
    [setState],
  );

  useEffect(() => {
    if (selectedId && !content.blocks[selectedId]) {
      setSelectedId('');
    }
  }, [content, selectedId]);

  useDidUpdateEffect(() => {
    setState((s) => ({ ...s, settingsPanes: undefined }));
    setViewState((v) => ({ ...v, subNav: undefined, showSettingsForId: '' }));
    const el = document.getElementById(`block-${selectedId}`);
    const scrollParent = el?.closest('.page-editor-scroll-container');
    if (!el || !scrollParent) {
      return;
    }
    scrollParent.scroll({ behavior: 'smooth', top: el.offsetTop });
  }, [selectedId]);

  async function deleteBlock(id: string) {
    const block = content.blocks[id];
    const def = blockDefinitions[block?.type];
    const allowDelete = def?.onDelete && (await def.onDelete({ state: block }));

    if (allowDelete === false) {
      return;
    }

    setSelectedId('');
    setContent((s) => ({
      ...s,
      blockIds: s.blockIds.filter((x) => id !== x),
      blocks: dissoc(id, s.blocks),
    }));
    setViewState((v) => ({ ...v, showUndoNotice: true }));
  }

  function newId() {
    return `${Date.now().toString(16)}-${content.blockIds.length.toString(16)}`;
  }

  function insertBlock(aboveId: string | undefined, state: SalesBlockState<any>) {
    setContent((s) => {
      const blockIds = [...s.blockIds];
      const insertAtIndex = aboveId ? Math.max(0, blockIds.indexOf(aboveId)) : blockIds.length;
      blockIds.splice(insertAtIndex, 0, state.id);
      return {
        ...s,
        blockIds,
        blocks: {
          ...s.blocks,
          [state.id]: state,
        },
      };
    });

    setSelectedId(state.id);
    setTimeout(() => scrollToSelectedSection({ force: true }));
  }

  async function cloneBlock(cloneId: string) {
    const block = content.blocks[cloneId];
    const afterId = content.blockIds[content.blockIds.indexOf(cloneId) + 1];
    insertBlock(afterId, { ...block, id: newId() });
  }

  const onKey = useDisposableMemo(() => {
    const result = {
      Delete: () => {},
      Escape: () => {},
      dispose: on(document, 'keydown', (e) => {
        const me = result as any;
        if (me[e.code] && e.target === document.body) {
          me[e.code]();
        }
      }),
    };

    return result;
  }, []);

  onKey.Escape = () => selectedId && setSelectedId('');
  onKey.Delete = () => selectedId && deleteBlock(selectedId);

  function addBlock(aboveId: string | undefined, type: string) {
    const def = blockDefinitions[type];
    const id = newId();
    const val: SalesBlockState<any> = {
      id,
      type: def.type,
      state: def.initialState,
    };

    if (!def.initialBlockState && content.blockIds.length) {
      const prevBg = content.blocks[content.blockIds[content.blockIds.length - 1]]?.bgcolor;
      if (!prevBg) {
        val.bgcolor = Color.gray50;
      }
    }

    insertBlock(aboveId, val);
  }

  function moveBlock(id: string, direction: number) {
    function swap(blockIds: string[]): string[] {
      const moveFrom = blockIds.indexOf(id);
      const moveTo = Math.min(Math.max(0, moveFrom + direction), blockIds.length - 1);
      const result = [...blockIds];
      const tmp = result[moveFrom];
      result[moveFrom] = result[moveTo];
      result[moveTo] = tmp;
      return result;
    }

    // Update content order
    setContent((s) => ({ ...s, blockIds: swap(s.blockIds) }));

    // Keep the moved block selected (scroll hook will handle scrolling)
    setSelectedId(id);
  }

  function handleInsertBlock(type: string) {
    addBlock(viewState.showNewSectionModal?.blockId, type);
    setViewState((v) => ({ ...v, showNewSectionModal: undefined }));
  }

  return (
    <PageBuilderProvider
      pageState={state}
      setPageState={setState}
      blockDefinitions={blockDefinitions}
    >
      <section class="flex flex-col md:flex-row grow max-h-screen">
        {/* Modals and Overlays */}
        {viewState.showNewSectionModal && (
          <NewSectionModal
            onHide={() => setViewState((v) => ({ ...v, showNewSectionModal: undefined }))}
            definitions={blockDefinitions}
            onInsert={handleInsertBlock}
          />
        )}

        <UndoNotice
          displayed={viewState.showUndoNotice}
          text="Section deleted."
          hide={() => setViewState((v) => ({ ...v, showUndoNotice: false }))}
          onClick={undoRedo.undo}
        />

        {/* Main Content Area */}
        <div
          class="bg-white shadow-md flex flex-col grow sales-page text-base text-gray-700 min-h-0"
          data-theme-styles
        >
          <PageEditorToolbar
            publicURL={publicURL}
            viewMode={viewMode}
            isDirty={isDirty}
            isConnected={isConnected}
            header={header}
            customNavItems={customNavItems}
            showMobileMenu={viewState.showMobileMenu}
            showCopyButton={showCopyButton}
            onViewModeChange={setViewMode}
            onResetPage={onReset ? onResetSalesPage : undefined}
            onToggleMobileMenu={() =>
              setViewState((v) => ({ ...v, showMobileMenu: !v.showMobileMenu }))
            }
            onCloseMobileMenu={() => setViewState((v) => ({ ...v, showMobileMenu: false }))}
          />

          <div class="relative flex-1 flex flex-col overflow-hidden">
            {/* Loading indicator - spinner near top of content area */}
            {!fontsLoaded && (
              <div class="absolute top-4 left-0 right-0 flex justify-center z-50">
                <div class="flex items-center gap-4 bg-white px-6 py-3 rounded-lg shadow-lg">
                  <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  <p class="text-base text-gray-600">Loading fonts...</p>
                </div>
              </div>
            )}

            {/* Page content with opacity transition */}
            <div
              class={`${fontsLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-200 flex flex-col flex-1 h-full overflow-hidden`}
            >
              <PageContent
                viewMode={viewMode}
                onUndo={undoRedo.undo}
                onRedo={undoRedo.redo}
                onAddBlock={() => setViewState((v) => ({ ...v, showNewSectionModal: {} }))}
              >
                <BlockList
                  content={content}
                  selectedId={selectedId}
                  undoRedoCount={viewState.undoRedoCount}
                  selection={state.selection}
                  blockDefinitions={blockDefinitions}
                  onDeleteBlock={deleteBlock}
                  onMoveBlock={moveBlock}
                  onCloneBlock={cloneBlock}
                  onSelectBlock={setSelectedId}
                  onSetContent={setContent}
                  onSetSelection={setSelection}
                  onAddBlock={(blockId?: string) =>
                    setViewState((v) => ({ ...v, showNewSectionModal: blockId ? { blockId } : {} }))
                  }
                  pageState={state}
                  setPageState={setState}
                />
              </PageContent>
            </div>
          </div>
        </div>
      </section>
    </PageBuilderProvider>
  );
}
